import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tooltip
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { extractDomainIdentifier } from './utils/campaignGrouping';

// Domain color schemes for consistent styling
const domainColors = {
  'knowledgia': { bg: '#e3f2fd', color: '#1565c0', light: '#bbdefb' },
  'thecozyguide': { bg: '#e8f5e9', color: '#2e7d32', light: '#c8e6c9' },
  'waveshare': { bg: '#fff3e0', color: '#e65100', light: '#ffe0b2' },
  'default': { bg: '#f3e5f5', color: '#7b1fa2', light: '#e1bee7' }
};

const getDomainColor = (domain) => {
  return domainColors[domain.toLowerCase()] || domainColors['default'];
};

/**
 * Compare domains within grouped campaigns and show win/loss summary
 * Only compares groups where we have campaigns from multiple domains
 * Only includes active campaigns in the comparison
 */
const DomainComparisonSummary = ({ groupedCampaigns, facebookCampaigns }) => {
  const comparison = useMemo(() => {
    if (!groupedCampaigns || groupedCampaigns.length === 0) {
      return null;
    }

    // Track wins and totals by domain for ROAS, EPC, and CVR
    const roasWins = {};
    const epcWins = {};
    const cvrWins = {};
    const domainTotals = {}; // Track total revenue, cost, demandClicks, clicks per domain
    let comparableGroups = 0;

    // Process each group
    groupedCampaigns.forEach(item => {
      if (!item.isGroup || item.campaigns.length < 2) {
        return; // Skip non-groups and single-campaign groups
      }

      // Filter to only include active campaigns
      const activeCampaigns = item.campaigns.filter(campaign => {
        const fbCampaign = facebookCampaigns?.[campaign.campaign_id];
        return fbCampaign?.is_active === true;
      });

      // Skip if we don't have at least 2 active campaigns
      if (activeCampaigns.length < 2) {
        return;
      }

      // Extract domains for each campaign in the group
      const campaignsWithDomains = activeCampaigns.map(campaign => {
        const domain = extractDomainIdentifier(campaign.campaign_name);
        const cost = parseFloat(campaign.cost || 0);
        const revenue = parseFloat(campaign.revenue || 0);
        const demandClicks = parseInt(campaign.demand_clicks || 0, 10);
        const clicks = parseInt(campaign.clicks || 0, 10);

        return {
          domain,
          cost,
          revenue,
          demandClicks,
          clicks,
          roas: cost > 0 ? revenue / cost : 0,
          epc: demandClicks > 0 ? revenue / demandClicks : 0,
          cvr: clicks > 0 ? (demandClicks / clicks) * 100 : 0,
          campaign
        };
      });

      // Get unique domains in this group
      const uniqueDomains = [...new Set(campaignsWithDomains.map(c => c.domain))];

      // Only compare if we have multiple different domains
      if (uniqueDomains.length < 2 || uniqueDomains.includes('unknown')) {
        return;
      }

      // Filter campaigns that have valid EPC/CVR data (need demandClicks > 0)
      const campaignsWithValidData = campaignsWithDomains.filter(c => c.epc > 0 || c.cvr > 0);

      // Skip this group if no campaigns have valid EPC/CVR data
      if (campaignsWithValidData.length < 2) {
        return;
      }

      comparableGroups++;

      // Only accumulate totals for campaigns in comparable groups
      campaignsWithDomains.forEach(c => {
        if (!domainTotals[c.domain]) {
          domainTotals[c.domain] = { revenue: 0, cost: 0, demandClicks: 0, clicks: 0 };
        }
        domainTotals[c.domain].revenue += c.revenue;
        domainTotals[c.domain].cost += c.cost;
        domainTotals[c.domain].demandClicks += c.demandClicks;
        domainTotals[c.domain].clicks += c.clicks;
      });

      // Find best ROAS in this group (use all campaigns with cost > 0)
      const campaignsWithRoas = campaignsWithDomains.filter(c => c.roas > 0);
      if (campaignsWithRoas.length >= 2) {
        const bestRoas = campaignsWithRoas.reduce((best, curr) =>
          curr.roas > best.roas ? curr : best
        );
        roasWins[bestRoas.domain] = (roasWins[bestRoas.domain] || 0) + 1;
      }

      // Find best EPC in this group (only campaigns with valid EPC)
      const campaignsWithEpc = campaignsWithDomains.filter(c => c.epc > 0);
      if (campaignsWithEpc.length >= 2) {
        const bestEpc = campaignsWithEpc.reduce((best, curr) =>
          curr.epc > best.epc ? curr : best
        );
        epcWins[bestEpc.domain] = (epcWins[bestEpc.domain] || 0) + 1;
      }

      // Find best CVR in this group (only campaigns with valid CVR)
      const campaignsWithCvr = campaignsWithDomains.filter(c => c.cvr > 0);
      if (campaignsWithCvr.length >= 2) {
        const bestCvr = campaignsWithCvr.reduce((best, curr) =>
          curr.cvr > best.cvr ? curr : best
        );
        cvrWins[bestCvr.domain] = (cvrWins[bestCvr.domain] || 0) + 1;
      }
    });

    if (comparableGroups === 0) {
      return null;
    }

    // Calculate total wins for percentage
    const totalRoasWins = Object.values(roasWins).reduce((sum, v) => sum + v, 0);
    const totalEpcWins = Object.values(epcWins).reduce((sum, v) => sum + v, 0);
    const totalCvrWins = Object.values(cvrWins).reduce((sum, v) => sum + v, 0);

    // Get ALL domains that participated in comparable groups (not just winners)
    const allDomains = Object.keys(domainTotals).filter(d => d !== 'unknown');
    const domainStats = allDomains.map(domain => {
      const totals = domainTotals[domain] || { revenue: 0, cost: 0, demandClicks: 0, clicks: 0 };
      const avgRoas = totals.cost > 0 ? totals.revenue / totals.cost : 0;
      const avgEpc = totals.demandClicks > 0 ? totals.revenue / totals.demandClicks : 0;
      const avgCvr = totals.clicks > 0 ? (totals.demandClicks / totals.clicks) * 100 : 0;
      const roasWinCount = roasWins[domain] || 0;
      const epcWinCount = epcWins[domain] || 0;
      const cvrWinCount = cvrWins[domain] || 0;

      return {
        domain,
        roasWins: roasWinCount,
        epcWins: epcWinCount,
        cvrWins: cvrWinCount,
        roasWinPct: totalRoasWins > 0 ? (roasWinCount / totalRoasWins) * 100 : 0,
        epcWinPct: totalEpcWins > 0 ? (epcWinCount / totalEpcWins) * 100 : 0,
        cvrWinPct: totalCvrWins > 0 ? (cvrWinCount / totalCvrWins) * 100 : 0,
        avgRoas,
        avgEpc,
        avgCvr,
        totalWins: roasWinCount + epcWinCount + cvrWinCount
      };
    }).sort((a, b) => b.avgRoas - a.avgRoas); // Sort by average ROAS

    // Find winners for each metric
    const roasWinner = domainStats.reduce((best, curr) => curr.roasWins > best.roasWins ? curr : best, domainStats[0]);
    const epcWinner = domainStats.reduce((best, curr) => curr.epcWins > best.epcWins ? curr : best, domainStats[0]);
    const cvrWinner = domainStats.reduce((best, curr) => curr.cvrWins > best.cvrWins ? curr : best, domainStats[0]);

    return {
      comparableGroups,
      domainStats,
      totalRoasWins,
      totalEpcWins,
      totalCvrWins,
      roasWinner: roasWinner.domain,
      epcWinner: epcWinner.domain,
      cvrWinner: cvrWinner.domain
    };
  }, [groupedCampaigns, facebookCampaigns]);

  if (!comparison) {
    return null;
  }

  // Helper to render a metric cell
  const MetricCell = ({ wins, total, avg, format, isWinner }) => (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      justifyContent: 'center'
    }}>
      {isWinner && wins > 0 && (
        <EmojiEventsIcon sx={{ fontSize: 14, color: '#ffc107' }} />
      )}
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          component="span"
          sx={{
            fontSize: '0.8rem',
            fontWeight: isWinner ? 700 : 500,
            display: 'block'
          }}
        >
          {format}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '0.65rem',
            color: 'text.secondary',
            display: 'block'
          }}
        >
          {wins}/{total} wins
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Paper sx={{ p: { xs: 1, md: 1.5 }, mb: 1.5, backgroundColor: 'rgba(25, 118, 210, 0.04)' }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
        Domain Comparison
        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
          Based on {comparison.comparableGroups} head-to-head tests
        </Typography>
      </Typography>

      {/* Table-style layout */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(100px, 1fr) repeat(3, minmax(80px, 1fr))' },
        gap: { xs: 1, sm: 0 },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden'
      }}>
        {/* Header row - hidden on mobile */}
        <Box sx={{
          display: { xs: 'none', sm: 'contents' }
        }}>
          <Box sx={{
            p: 1,
            backgroundColor: 'grey.100',
            fontWeight: 600,
            fontSize: '0.75rem',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            Domain
          </Box>
          <Box sx={{
            p: 1,
            backgroundColor: 'grey.100',
            fontWeight: 600,
            fontSize: '0.75rem',
            textAlign: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            ROAS
          </Box>
          <Box sx={{
            p: 1,
            backgroundColor: 'grey.100',
            fontWeight: 600,
            fontSize: '0.75rem',
            textAlign: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            EPC
          </Box>
          <Box sx={{
            p: 1,
            backgroundColor: 'grey.100',
            fontWeight: 600,
            fontSize: '0.75rem',
            textAlign: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            CVR
          </Box>
        </Box>

        {/* Data rows */}
        {comparison.domainStats.map((stat, index) => {
          const colorScheme = getDomainColor(stat.domain);
          const isLastRow = index === comparison.domainStats.length - 1;

          return (
            <React.Fragment key={stat.domain}>
              {/* Mobile: Card-style layout */}
              <Box sx={{
                display: { xs: 'block', sm: 'none' },
                p: 1.5,
                backgroundColor: colorScheme.bg,
                borderBottom: isLastRow ? 'none' : '1px solid',
                borderColor: 'divider'
              }}>
                <Typography sx={{
                  fontWeight: 600,
                  color: colorScheme.color,
                  fontSize: '0.85rem',
                  mb: 1
                }}>
                  {stat.domain}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Tooltip title={`Won ${stat.roasWins} of ${comparison.totalRoasWins} ROAS comparisons`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {stat.domain === comparison.roasWinner && stat.roasWins > 0 && (
                        <EmojiEventsIcon sx={{ fontSize: 14, color: '#ffc107' }} />
                      )}
                      <Typography sx={{ fontSize: '0.75rem' }}>
                        <strong>ROAS:</strong> {stat.avgRoas.toFixed(2)} ({stat.roasWins}/{comparison.totalRoasWins})
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title={`Won ${stat.epcWins} of ${comparison.totalEpcWins} EPC comparisons`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {stat.domain === comparison.epcWinner && stat.epcWins > 0 && (
                        <EmojiEventsIcon sx={{ fontSize: 14, color: '#ffc107' }} />
                      )}
                      <Typography sx={{ fontSize: '0.75rem' }}>
                        <strong>EPC:</strong> ${stat.avgEpc.toFixed(2)} ({stat.epcWins}/{comparison.totalEpcWins})
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title={`Won ${stat.cvrWins} of ${comparison.totalCvrWins} CVR comparisons`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {stat.domain === comparison.cvrWinner && stat.cvrWins > 0 && (
                        <EmojiEventsIcon sx={{ fontSize: 14, color: '#ffc107' }} />
                      )}
                      <Typography sx={{ fontSize: '0.75rem' }}>
                        <strong>CVR:</strong> {stat.avgCvr.toFixed(1)}% ({stat.cvrWins}/{comparison.totalCvrWins})
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>

              {/* Desktop: Table row */}
              <Box sx={{
                display: { xs: 'none', sm: 'contents' }
              }}>
                {/* Domain name */}
                <Box sx={{
                  p: 1,
                  backgroundColor: colorScheme.bg,
                  borderBottom: isLastRow ? 'none' : '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Typography sx={{
                    fontWeight: 600,
                    color: colorScheme.color,
                    fontSize: '0.8rem'
                  }}>
                    {stat.domain}
                  </Typography>
                </Box>

                {/* ROAS */}
                <Tooltip title={`Won ${stat.roasWins} of ${comparison.totalRoasWins} head-to-head ROAS comparisons`}>
                  <Box sx={{
                    p: 1,
                    backgroundColor: stat.domain === comparison.roasWinner && stat.roasWins > 0 ? colorScheme.light : 'transparent',
                    borderBottom: isLastRow ? 'none' : '1px solid',
                    borderColor: 'divider'
                  }}>
                    <MetricCell
                      wins={stat.roasWins}
                      total={comparison.totalRoasWins}
                      avg={stat.avgRoas}
                      format={stat.avgRoas.toFixed(2)}
                      isWinner={stat.domain === comparison.roasWinner}
                    />
                  </Box>
                </Tooltip>

                {/* EPC */}
                <Tooltip title={`Won ${stat.epcWins} of ${comparison.totalEpcWins} head-to-head EPC comparisons`}>
                  <Box sx={{
                    p: 1,
                    backgroundColor: stat.domain === comparison.epcWinner && stat.epcWins > 0 ? colorScheme.light : 'transparent',
                    borderBottom: isLastRow ? 'none' : '1px solid',
                    borderColor: 'divider'
                  }}>
                    <MetricCell
                      wins={stat.epcWins}
                      total={comparison.totalEpcWins}
                      avg={stat.avgEpc}
                      format={`$${stat.avgEpc.toFixed(2)}`}
                      isWinner={stat.domain === comparison.epcWinner}
                    />
                  </Box>
                </Tooltip>

                {/* CVR */}
                <Tooltip title={`Won ${stat.cvrWins} of ${comparison.totalCvrWins} head-to-head CVR comparisons`}>
                  <Box sx={{
                    p: 1,
                    backgroundColor: stat.domain === comparison.cvrWinner && stat.cvrWins > 0 ? colorScheme.light : 'transparent',
                    borderBottom: isLastRow ? 'none' : '1px solid',
                    borderColor: 'divider'
                  }}>
                    <MetricCell
                      wins={stat.cvrWins}
                      total={comparison.totalCvrWins}
                      avg={stat.avgCvr}
                      format={`${stat.avgCvr.toFixed(1)}%`}
                      isWinner={stat.domain === comparison.cvrWinner}
                    />
                  </Box>
                </Tooltip>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Paper>
  );
};

export default DomainComparisonSummary;
